import { type SuretyAccountId } from "@inrev/common";
import { useAdminUpdateProfitability } from "@portal/api/financial-period/admin";
import { useCallback, useEffect, useState } from "react";

type EditableProfitabilityState =
	| { kind: "display"; profitable: boolean | null }
	| { kind: "editing"; selected: boolean | null }
	| { kind: "submitting"; selected: boolean };

type Options = {
	accountId: SuretyAccountId;
	isFye?: boolean;
	isProfitable?: boolean;
	startInEditMode?: boolean;
	onEditClose?: () => void;
};

export type UseEditableProfitabilityResult = {
	displayProfitable: boolean | null;
	selectedProfitable: boolean | null;
	isEditing: boolean;
	mutationError: Error | null;
	startEditing: () => void;
	cancelEditing: () => void;
	selectProfitable: (value: boolean) => void;
	submit: () => void;
	dismissError: () => void;
	canSubmit: boolean;
};

const toDisplayState = (isProfitable?: boolean): EditableProfitabilityState => ({
	kind: "display",
	profitable: isProfitable === undefined ? null : isProfitable,
});

export const useEditableProfitability = ({
	accountId,
	isFye,
	isProfitable,
	startInEditMode = false,
	onEditClose,
}: Options): UseEditableProfitabilityResult => {
	const [state, setState] = useState<EditableProfitabilityState>(() =>
		startInEditMode ? { kind: "editing", selected: null } : toDisplayState(isProfitable),
	);

	useEffect(() => {
		setState((current) => (current.kind === "display" ? toDisplayState(isProfitable) : current));
	}, [isProfitable]);

	const { updateProfitability, updateProfitabilityError, dismissUpdateProfitabilityError } =
		useAdminUpdateProfitability(accountId, isFye);

	const startEditing = useCallback(() => {
		setState({ kind: "editing", selected: null });
	}, []);

	const cancelEditing = useCallback(() => {
		setState(toDisplayState(isProfitable));
		if (startInEditMode && onEditClose) {
			onEditClose();
		}
	}, [isProfitable, startInEditMode, onEditClose]);

	const selectProfitable = useCallback((value: boolean) => {
		setState((current) => (current.kind === "editing" ? { ...current, selected: value } : current));
	}, []);

	const submit = useCallback(() => {
		if (state.kind !== "editing" || state.selected === null) return;
		const selected = state.selected;

		setState({ kind: "submitting", selected });

		updateProfitability({
			data: { profitable: selected },
			onSuccess: () => {
				setState({ kind: "display", profitable: selected });
			},
			onError: () => {
				setState({ kind: "editing", selected });
			},
		});
	}, [state, updateProfitability]);

	const mutationError = updateProfitabilityError ?? null;

	const dismissError = useCallback(() => {
		dismissUpdateProfitabilityError();
	}, [dismissUpdateProfitabilityError]);

	const canSubmit = state.kind === "editing" && state.selected !== null;
	const isEditing = state.kind !== "display";
	const displayProfitable = state.kind === "display" ? state.profitable : null;
	const selectedProfitable =
		state.kind === "editing" || state.kind === "submitting" ? state.selected : null;

	return {
		displayProfitable,
		selectedProfitable,
		isEditing,
		mutationError,
		startEditing,
		cancelEditing,
		selectProfitable,
		submit,
		dismissError,
		canSubmit,
	};
};
